<script>
import { readConversationMovePayload } from '@modules/aawz-intelligence-v2/utils/conversationMoveDrag'

// Linha de pasta: ícone + bolinha da cor, nome, chip de contagem, e drop-target
// pra conversa arrastada (espelha task-sidebar.vue).
export default {
  name: 'IntelligenceV2FolderItem',
  props: {
    folder: {
      type: Object,
      required: true,
    },
    count: {
      type: Number,
      default: 0,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dragOver: false,
    }
  },
  computed: {
    dotStyle() {
      return { backgroundColor: this.folder.color || 'transparent' }
    },
    countLabel() {
      const n = this.count || 0
      return n === 1 ? '1 conversa' : n + ' conversas'
    },
    chevronName() {
      return this.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right'
    },
    menuActions() {
      return [
        { label: 'Editar', icon: 'edit-pencil', onClick: this.emitRename },
        {
          label: 'Excluir',
          icon: 'trash',
          danger: true,
          onClick: this.emitDelete,
        },
      ]
    },
  },
  methods: {
    toggle() {
      this.$emit('toggle', this.folder.id)
    },
    emitRename() {
      this.$emit('rename', this.folder)
    },
    emitDelete() {
      this.$emit('delete', this.folder)
    },
    onDragOver(event) {
      // preventDefault habilita o drop HTML5 e mostra o highlight.
      if (event) event.preventDefault()
      this.dragOver = true
    },
    onDragLeave() {
      this.dragOver = false
    },
    onDrop(event) {
      if (event) event.preventDefault()
      this.dragOver = false
      const dt = event ? event.dataTransfer : null
      const payload = readConversationMovePayload(dt)
      // Payload ausente/malformado → no-op (sem erro).
      if (!payload || !payload.conversationId) return
      // Solto na mesma pasta que ja esta → no-op (sem request). Compara como
      // string pois o payload sempre serializa currentFolderId assim.
      if (payload.currentFolderId === String(this.folder.id)) return
      this.$emit('drop-conversation', {
        conversationId: payload.conversationId,
        folderId: this.folder.id,
      })
    },
  },
}
</script>

<template>
  <div
    class="folder-item ia-texto-corpo d-flex align-center py-1 px-2"
    :class="{ 'folder-item--drag-over': dragOver }"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <button
      type="button"
      class="folder-item__toggle d-flex align-center ma-0 pa-0"
      :aria-expanded="expanded ? 'true' : 'false'"
      @click="toggle"
    >
      <icon-svg
        :name="chevronName"
        :size="16"
        color="#8E8E8E"
        class="folder-item__chevron flex-shrink-0 mr-1"
      />

      <span class="folder-item__icon-wrap flex-shrink-0 mr-2">
        <icon-svg name="folder-outline" :size="14" color="#8E8E8E" />
        <span class="folder-item__dot" :style="dotStyle" />
      </span>

      <span
        class="folder-item__name ia-texto-corpo az-ds-body text-truncate"
        :title="folder.name"
      >
        {{ folder.name }}
      </span>

      <span
        class="az-ds-badge-count az-neutral flex-shrink-0 ml-auto"
        :aria-label="countLabel"
        >{{ count || 0 }}</span
      >
    </button>

    <div class="folder-item__menu flex-shrink-0" @click.stop>
      <BaseActionsMenu
        icon-only
        label="Ações da pasta"
        :actions="menuActions"
      />
    </div>
  </div>
</template>

<style lang="sass" scoped>
// A linha da pasta é um ITEM DE LISTA, não um card: em 44px de altura com o
// nome em 16px ela pesava mais que as conversas (47px COM duas linhas de
// texto) e as pastas ficavam soltas umas das outras. 13px e padding menor
// aproximam a densidade da lista de conversas logo abaixo.
.folder-item
  border-radius: var(--radius-sm)
  transition: background 0.12s, box-shadow 0.12s

  &__name
    // Não estica: sem isto o nome ocupa a sobra e empurra o badge para junto
    // dele, no meio da linha, em vez de deixá-lo encostar na direita.
    flex: 0 1 auto

  &:hover
    background: var(--neutral-100)

  &--drag-over
    background: var(--neutral-100)
    outline: 2px dashed var(--primary)
    outline-offset: -2px

  &__toggle
    flex: 1
    min-width: 0
    background: none
    border: 0
    font: inherit
    color: inherit
    text-align: left
    cursor: pointer

  &__icon-wrap
    position: relative
    display: inline-flex

  &__dot
    position: absolute
    bottom: -1px
    right: -1px
    width: 8.5px
    height: 8.5px
    border-radius: 50%
    border: 1.5px solid var(--surface, white)

  &__name
    display: block
    flex-shrink: 1
    max-width: 140px
    // az-ds-body cobre font-family/size/color; peso 600 se mantém local pra
    // destacar o nome da pasta (az-ds-body é 400 por padrão).
    font-weight: 600
    line-height: 1.35
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

  &__menu
    opacity: 0
    transition: opacity 0.12s

  &:hover &__menu
    opacity: 1
</style>
